import { useMemo } from "react";
import { Link } from "react-router-dom";
import { UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PaperCard } from "@/components/ui/paper-card";
import { EmptyState } from "@/components/empty-state";
import { CanEdit, useEditorCopy } from "@/lib/project-access";
import { PersonaPanel } from "@/components/experience/persona-panel";
import type { ScenarioBinding } from "./scenario-detail-types";

interface PersonasSectionProps {
  projectId: string;
  outlineId: string;
  bindings: ScenarioBinding[];
  hashPersonaId: string | null;
}

export function PersonasSection({
  projectId,
  outlineId,
  bindings,
  hashPersonaId,
}: PersonasSectionProps) {
  // Default expansion rule (when no hash anchor narrows the choice):
  //   - Single persona → expand it (low cognitive load, no choice to make)
  //   - Multi persona  → expand the one with critical findings (eye lands
  //                       on what needs attention); if none, all collapsed
  //                       (the user picks who to dig into)
  const autoExpandPersonaId = useMemo(() => {
    if (hashPersonaId) return hashPersonaId;
    if (bindings.length === 1) return bindings[0].personaId;
    const critical = bindings.find((b) => b.hasCriticalOpen);
    return critical?.personaId ?? null;
  }, [bindings, hashPersonaId]);

  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold tracking-tight">
          Personas
          <span className="ml-2 text-sm font-normal tabular-nums text-muted-foreground">
            · {bindings.length} cast
          </span>
        </h2>
      </div>
      <PaperCard padding="sm" className="divide-y divide-border/60 p-0">
        {bindings.map((binding) => (
          <PersonaPanel
            key={binding.scenarioId}
            projectId={projectId}
            outlineId={outlineId}
            personaId={binding.personaId}
            scenarioId={binding.scenarioId}
            defaultExpanded={autoExpandPersonaId === binding.personaId}
          />
        ))}
      </PaperCard>
    </section>
  );
}

export function EmptyPersonasState({
  projectId,
  outlineId,
}: {
  projectId: string;
  outlineId: string;
}) {
  const description = useEditorCopy(
    "Cast a persona to this scenario to make it runnable. Each cast creates a walkthrough lens — the same situation seen through that person's eyes.",
    "No persona is cast in this scenario yet, so it can't be run. Ask an editor to cast one.",
  );
  return (
    <EmptyState
      icon={UsersRound}
      title="No personas cast yet"
      description={description}
      actions={
        <CanEdit>
          <Button asChild>
            <Link to={`/projects/${projectId}/scenarios/${outlineId}/edit`}>
              Cast your first persona
            </Link>
          </Button>
        </CanEdit>
      }
    />
  );
}
